import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  Button,
  Alert,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

type DomainVerificationProps = {
  domain: string
  verification: {
    type: 'TXT' | string
    domain: string
    value: string
  }
  onVerifyClick?: () => void
}

export function DomainVerificationCard({
  domain,
  verification,
  onVerifyClick,
}: DomainVerificationProps) {
  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value)
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">
              Verificação de domínio necessária
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Para garantir o funcionamento correto do Pixel do Facebook,
              precisamos validar a propriedade do subdomínio.
            </Typography>
          </Box>

          <Alert severity="warning">
            O domínio <strong>{domain}</strong> já foi usado em outro projeto da
            Vercel e precisa ser verificado.
          </Alert>

          <Divider />

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Registro DNS a ser criado
            </Typography>

            <Stack spacing={1}>
              <Field
                label="Tipo"
                value={verification.type}
                onCopy={handleCopy}
              />
              <Field
                label="Host"
                value={verification.domain.split('.')[0]}
                onCopy={handleCopy}
              />
              <Field
                label="Valor"
                value={verification.value}
                onCopy={handleCopy}
                multiline
              />
              <Field label="TTL" value="Auto ou 300" />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2">Passo a passo</Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>
                Acesse o painel DNS do seu domínio (Registro.br, Cloudflare,
                etc)
              </li>
              <li>
                Crie um novo registro do tipo <strong>TXT</strong>
              </li>
              <li>Preencha os campos exatamente como acima</li>
              <li>Salve e aguarde alguns minutos</li>
              <li>Volte aqui e clique em “Verificar domínio”</li>
            </ul>
          </Box>

          <Alert severity="info">
            Esse apontamento é usado apenas para verificação e pode ser removido
            após a confirmação.
          </Alert>

          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={onVerifyClick}>
              Já adicionei o TXT, verificar agora
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

type FieldProps = {
  label: string
  value: string
  onCopy?: (value: string) => void
  multiline?: boolean
}

function Field({ label, value, onCopy, multiline }: FieldProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          padding: 1,
          fontFamily: 'monospace',
          wordBreak: 'break-all',
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: multiline ? 'normal' : 'nowrap' }}
        >
          {value}
        </Typography>

        {onCopy && (
          <Tooltip title="Copiar">
            <IconButton size="small" onClick={() => onCopy(value)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}
